/**
 * Utilitare pentru gestionarea timezone-urilor în frontend
 */

// Timezone-ul default pentru România
export const DEFAULT_TIMEZONE = 'Europe/Bucharest';

/**
 * Obține timezone-ul browserului utilizatorului
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
};

/**
 * Convertește un string datetime la obiect Date cu timezone-ul local
 */
export const parseDateTime = (dateTimeString: string): Date => {
  if (!dateTimeString) return new Date();
  
  // Dacă string-ul nu are timezone info, presupunem că este în timezone-ul local
  if (!dateTimeString.includes('T') || (!dateTimeString.includes('+') && !dateTimeString.endsWith('Z'))) {
    return new Date(dateTimeString);
  }
  
  return new Date(dateTimeString);
};

/**
 * Formatează un datetime pentru afișare în timezone-ul local
 */
export const formatDateTime = (dateTime: string | Date, options?: Intl.DateTimeFormatOptions): string => {
  if (!dateTime) return '';
  
  const date = typeof dateTime === 'string' ? parseDateTime(dateTime) : dateTime;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getUserTimezone(),
    ...options
  };
  
  return date.toLocaleString('ro-RO', defaultOptions);
};

/**
 * Formatează doar data (fără ora)
 */
export const formatDate = (dateTime: string | Date): string => {
  return formatDateTime(dateTime, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: undefined,
    minute: undefined
  });
};

/**
 * Formatează doar ora
 */
export const formatTime = (dateTime: string | Date): string => {
  return formatDateTime(dateTime, {
    year: undefined,
    month: undefined,
    day: undefined,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Convertește un datetime local la format ISO pentru trimitere la backend
 */
export const toISOString = (dateTime: Date | string): string => {
  const date = typeof dateTime === 'string' ? parseDateTime(dateTime) : dateTime;
  return date.toISOString();
};

/**
 * Creează un datetime local din componentele date
 */
export const createLocalDateTime = (year: number, month: number, day: number, hour: number = 0, minute: number = 0): Date => {
  return new Date(year, month - 1, day, hour, minute);
};

/**
 * Convertește un datetime-local input value la Date object
 */
export const fromDateTimeLocalInput = (value: string): Date => {
  if (!value) return new Date();
  
  // Format: YYYY-MM-DDTHH:mm
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0];
  
  return createLocalDateTime(year, month, day, hour, minute);
};

/**
 * Convertește un Date object la format datetime-local input value
 */
export const toDateTimeLocalInput = (date: Date | string): string => {
  const d = typeof date === 'string' ? parseDateTime(date) : date;
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hour = String(d.getHours()).padStart(2, '0');
  const minute = String(d.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

/**
 * Calculează durata între două datetime-uri în ore
 */
export const calculateDurationInHours = (startDateTime: string | Date, endDateTime: string | Date): number => {
  const start = typeof startDateTime === 'string' ? parseDateTime(startDateTime) : startDateTime;
  const end = typeof endDateTime === 'string' ? parseDateTime(endDateTime) : endDateTime;
  
  const diffMs = end.getTime() - start.getTime();
  return diffMs / (1000 * 60 * 60); // Convert to hours
};

/**
 * Verifică dacă două datetime-uri sunt în aceeași zi
 */
export const isSameDay = (date1: string | Date, date2: string | Date): boolean => {
  const d1 = typeof date1 === 'string' ? parseDateTime(date1) : date1;
  const d2 = typeof date2 === 'string' ? parseDateTime(date2) : date2;
  
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

/**
 * Obține data/ora curentă în timezone-ul local
 */
export const now = (): Date => {
  return new Date();
};

/**
 * Adaugă ore la un datetime
 */
export const addHours = (dateTime: Date | string, hours: number): Date => {
  const date = typeof dateTime === 'string' ? parseDateTime(dateTime) : dateTime;
  const result = new Date(date);
  result.setHours(result.getHours() + hours);
  return result;
};

/**
 * Adaugă zile la un datetime
 */
export const addDays = (dateTime: Date | string, days: number): Date => {
  const date = typeof dateTime === 'string' ? parseDateTime(dateTime) : dateTime;
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};
